import { useState, useEffect } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Snackbar,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import HistoryIcon from '@mui/icons-material/History'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import LinkIcon from '@mui/icons-material/Link'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid'
import { paymentSettingsService } from '../services'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'

const PAYMENT_TYPES = {
  bank_account: 'Bank Account',
  mfs_account: 'MFS Number',
  payment_link: 'Payment Link',
  payment_credential: 'Payment Credential',
}

const PaymentSettingsPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.PAYMENT_SETTINGS,
    'write',
  )
  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [bankAccounts, setBankAccounts] = useState([])
  const [mfsAccounts, setMfsAccounts] = useState([])
  const [paymentLinks, setPaymentLinks] = useState([])
  const [paymentCredentials, setPaymentCredentials] = useState([])
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [formErrors, setFormErrors] = useState({})
  const [dialogError, setDialogError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [auditItem, setAuditItem] = useState(null)
  const [auditLogs, setAuditLogs] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState(null)

  const tabs = [
    { label: 'Bank Accounts', value: 'bank_account', icon: AccountBalanceIcon },
    { label: 'MFS Numbers', value: 'mfs_account', icon: PhoneAndroidIcon },
    { label: 'Payment Links', value: 'payment_link', icon: LinkIcon },
    { label: 'Payment Credentials', value: 'payment_credential', icon: VpnKeyIcon },
  ]

  useEffect(() => {
    loadPaymentSettings()
  }, [])

  const loadPaymentSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [banks, mfs, links, credentials] = await Promise.all([
        paymentSettingsService.getAll('bank_account'),
        paymentSettingsService.getAll('mfs_account'),
        paymentSettingsService.getAll('payment_link'),
        paymentSettingsService.getAll('payment_credential'),
      ])
      
      setBankAccounts(banks || [])
      setMfsAccounts(mfs || [])
      setPaymentLinks(links || [])
      setPaymentCredentials(credentials || [])
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load payment settings')
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleOpenDialog = (item = null) => {
    const currentType = tabs[activeTab].value
    if (item) {
      setEditingItem(item)
      setFormData({
        type: currentType,
        ...item,
      })
    } else {
      setEditingItem(null)
      setFormData({
        type: currentType,
        account_holder_name: '',
        bank_name: '',
        branch_name: '',
        account_number: '',
        routing_number: '',
        phone_number: '',
        link: '',
        name: '',
        description: '',
        platform_name: '',
        credential_detail: '',
      })
    }
    setFormErrors({})
    setDialogError(null)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingItem(null)
    setFormData({})
    setFormErrors({})
    setDialogError(null)
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const errors = {}
    const currentType = tabs[activeTab].value

    if (currentType === 'bank_account') {
      if (!formData.account_holder_name) errors.account_holder_name = 'Required'
      if (!formData.bank_name) errors.bank_name = 'Required'
      if (!formData.branch_name) errors.branch_name = 'Required'
      if (!formData.account_number) {
        errors.account_number = 'Required'
      } else if (!/^\d+$/.test(String(formData.account_number))) {
        errors.account_number = 'Must contain digits only'
      } else if (String(formData.account_number).length > 50) {
        errors.account_number = 'Must be at most 50 digits'
      }
      if (!formData.routing_number) {
        errors.routing_number = 'Required'
      } else if (!/^\d{9}$/.test(formData.routing_number)) {
        errors.routing_number = 'Must be exactly 9 digits'
      }
      if (!formData.phone_number) {
        errors.phone_number = 'Required'
      } else if (!/^0\d{10}$/.test(formData.phone_number)) {
        errors.phone_number = 'Must be 11 digits starting with 0'
      }
    } else if (currentType === 'payment_link') {
      if (!formData.link) errors.link = 'Required'
      if (!formData.name) errors.name = 'Required'
    } else if (currentType === 'mfs_account') {
      if (!formData.platform_name?.trim()) {
        errors.platform_name = 'Required'
      }
      if (!formData.phone_number) {
        errors.phone_number = 'Required'
      } else if (!/^0\d{10}$/.test(formData.phone_number)) {
        errors.phone_number = 'Must be 11 digits starting with 0'
      }
    } else if (currentType === 'payment_credential') {
      if (!formData.platform_name) errors.platform_name = 'Required'
      if (!formData.credential_detail) errors.credential_detail = 'Required'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    try {
      setSubmitting(true)
      setDialogError(null)

      // Prepare data based on type
      const currentType = tabs[activeTab].value
      const submitData = { type: currentType }

      if (currentType === 'bank_account') {
        submitData.account_holder_name = formData.account_holder_name
        submitData.bank_name = formData.bank_name
        submitData.branch_name = formData.branch_name
        submitData.account_number = formData.account_number
        submitData.routing_number = formData.routing_number
        submitData.phone_number = formData.phone_number
      } else if (currentType === 'payment_link') {
        submitData.link = formData.link
        submitData.name = formData.name
        submitData.description = formData.description || ''
      } else if (currentType === 'mfs_account') {
        submitData.platform_name = String(formData.platform_name || '').trim()
        submitData.phone_number = formData.phone_number
      } else if (currentType === 'payment_credential') {
        submitData.platform_name = formData.platform_name
        submitData.description = formData.description || ''
        submitData.credential_detail = formData.credential_detail
      }

      if (editingItem) {
        await paymentSettingsService.update(editingItem.id, submitData)
        setSuccessMessage('Payment setting updated successfully')
      } else {
        await paymentSettingsService.create(submitData)
        setSuccessMessage('Payment setting created successfully')
      }

      handleCloseDialog()
      loadPaymentSettings()
    } catch (err) {
      const raw = err.response?.data?.message
      const messages = Array.isArray(raw) ? raw : raw ? [raw] : []
      const nextErrors = {}
      for (const line of messages) {
        const text = String(line)
        const lower = text.toLowerCase()
        if (lower.includes('account number')) nextErrors.account_number = text
        else if (lower.includes('routing')) nextErrors.routing_number = text
        else if (lower.includes('phone') || lower.includes('contact number'))
          nextErrors.phone_number = text
        else if (lower.includes('mfs') || lower.includes('platform_name'))
          nextErrors.platform_name = text
        else if (lower.includes('account holder')) nextErrors.account_holder_name = text
        else if (lower.includes('bank name')) nextErrors.bank_name = text
        else if (lower.includes('branch')) nextErrors.branch_name = text
      }
      if (Object.keys(nextErrors).length > 0) {
        setFormErrors((prev) => ({ ...prev, ...nextErrors }))
      }
      setDialogError(
        messages.length > 0
          ? messages.join(' ')
          : 'Failed to save payment setting',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteClick = (item) => {
    setItemToDelete(item)
    setDeleteDialogOpen(true)
  }

  const auditActionLabel = (action) => {
    if (action === 'PAYMENT_SETTINGS_CREATED') return 'Created'
    if (action === 'PAYMENT_SETTINGS_UPDATED') return 'Updated'
    return action || '—'
  }

  const settingLabel = (item) => {
    if (!item) return ''
    if (item.type === 'bank_account') return item.account_holder_name || item.bank_name || ''
    if (item.type === 'mfs_account') return item.platform_name || ''
    if (item.type === 'payment_link') return item.name || ''
    return item.platform_name || ''
  }

  const handleOpenAudit = async (item) => {
    setAuditItem(item)
    setAuditLogs([])
    setAuditError(null)
    setAuditLoading(true)
    try {
      const data = await paymentSettingsService.getAuditLogs(item.id)
      setAuditLogs(Array.isArray(data) ? data : [])
    } catch (err) {
      setAuditError(err.response?.data?.message || 'Failed to load audit log')
    } finally {
      setAuditLoading(false)
    }
  }

  const handleCloseAudit = () => {
    setAuditItem(null)
    setAuditLogs([])
    setAuditError(null)
  }

  const renderActions = (item) => (
    <TableCell align="right">
      <Tooltip title="Audit log">
        <IconButton size="small" onClick={() => handleOpenAudit(item)} sx={{ color: '#475569' }}>
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {canWrite && (
        <>
          <IconButton size="small" onClick={() => handleOpenDialog(item)} color="primary">
            <EditIcon />
          </IconButton>
          <IconButton size="small" onClick={() => handleDeleteClick(item)} color="error">
            <DeleteIcon />
          </IconButton>
        </>
      )}
    </TableCell>
  )

  const handleDeleteConfirm = async () => {
    try {
      await paymentSettingsService.delete(itemToDelete.id)
      setSuccessMessage('Payment setting deleted successfully')
      setDeleteDialogOpen(false)
      setItemToDelete(null)
      loadPaymentSettings()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete payment setting')
      setDeleteDialogOpen(false)
    }
  }

  const renderTable = (items, type) => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )
    }

    if (items.length === 0) {
      return (
        <Box p={4} textAlign="center">
          <Typography variant="body1" color="text.secondary">
            No {PAYMENT_TYPES[type]} settings found. Click the "+ Add" button to create one.
          </Typography>
        </Box>
      )
    }

    if (type === 'bank_account') {
      return (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Account Holder Name</TableCell>
                <TableCell>Bank Name</TableCell>
                <TableCell>Branch Name</TableCell>
                <TableCell>Account Number</TableCell>
                <TableCell>Routing Number</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.account_holder_name}</TableCell>
                  <TableCell>{item.bank_name}</TableCell>
                  <TableCell>{item.branch_name}</TableCell>
                  <TableCell>{item.account_number}</TableCell>
                  <TableCell>{item.routing_number}</TableCell>
                  <TableCell>{item.phone_number || '—'}</TableCell>
                  {renderActions(item)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )
    }

    if (type === 'mfs_account') {
      return (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>MFS Name</TableCell>
                <TableCell>Contact Number</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.platform_name}</TableCell>
                  <TableCell>{item.phone_number}</TableCell>
                  {renderActions(item)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )
    }

    if (type === 'payment_link') {
      return (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Link</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2', textDecoration: 'none' }}
                    >
                      {item.link}
                    </a>
                  </TableCell>
                  <TableCell>{item.description || '-'}</TableCell>
                  {renderActions(item)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )
    }

    if (type === 'payment_credential') {
      return (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Platform Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Credential Detail</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.platform_name}</TableCell>
                  <TableCell>{item.description || '-'}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.credential_detail}
                    </Typography>
                  </TableCell>
                  {renderActions(item)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )
    }
  }

  const renderForm = () => {
    const currentType = tabs[activeTab].value

    if (currentType === 'bank_account') {
      return (
        <>
          <TextField
            fullWidth
            label="Account Holder Name"
            value={formData.account_holder_name || ''}
            onChange={(e) => handleInputChange('account_holder_name', e.target.value)}
            error={!!formErrors.account_holder_name}
            helperText={formErrors.account_holder_name}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Bank Name"
            value={formData.bank_name || ''}
            onChange={(e) => handleInputChange('bank_name', e.target.value)}
            error={!!formErrors.bank_name}
            helperText={formErrors.bank_name}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Branch Name"
            value={formData.branch_name || ''}
            onChange={(e) => handleInputChange('branch_name', e.target.value)}
            error={!!formErrors.branch_name}
            helperText={formErrors.branch_name}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Account Number"
            value={formData.account_number || ''}
            onChange={(e) =>
              handleInputChange(
                'account_number',
                e.target.value.replace(/\D/g, '').slice(0, 50),
              )
            }
            error={!!formErrors.account_number}
            helperText={formErrors.account_number || 'Digits only, up to 50 characters'}
            margin="normal"
            required
            inputProps={{ maxLength: 50 }}
          />
          <TextField
            fullWidth
            label="Routing Number (9 digits)"
            value={formData.routing_number || ''}
            onChange={(e) => handleInputChange('routing_number', e.target.value)}
            error={!!formErrors.routing_number}
            helperText={formErrors.routing_number || 'Must be exactly 9 digits'}
            margin="normal"
            required
            inputProps={{ maxLength: 9 }}
          />
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phone_number || ''}
            onChange={(e) => handleInputChange('phone_number', e.target.value)}
            error={!!formErrors.phone_number}
            helperText={
              formErrors.phone_number || 'Must be 11 digits starting with 0 (e.g. 01700000000)'
            }
            margin="normal"
            required
            inputProps={{ maxLength: 11 }}
          />
        </>
      )
    }

    if (currentType === 'mfs_account') {
      return (
        <>
          <TextField
            fullWidth
            label="MFS Name"
            value={formData.platform_name || ''}
            onChange={(e) => handleInputChange('platform_name', e.target.value)}
            error={!!formErrors.platform_name}
            helperText={
              formErrors.platform_name ||
              'e.g. bKash, Nagad, Rocket — shown to grocers'
            }
            margin="normal"
            required
            placeholder="bKash"
          />
          <TextField
            fullWidth
            label="Contact Number"
            value={formData.phone_number || ''}
            onChange={(e) =>
              handleInputChange(
                'phone_number',
                e.target.value.replace(/\D/g, '').slice(0, 11),
              )
            }
            error={!!formErrors.phone_number}
            helperText={
              formErrors.phone_number ||
              'Must be 11 digits starting with 0 (e.g. 01700000000)'
            }
            margin="normal"
            required
            inputProps={{ maxLength: 11 }}
            placeholder="01700000000"
          />
        </>
      )
    }

    if (currentType === 'payment_link') {
      return (
        <>
          <TextField
            fullWidth
            label="Name"
            value={formData.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!formErrors.name}
            helperText={formErrors.name}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Link (URL)"
            value={formData.link || ''}
            onChange={(e) => handleInputChange('link', e.target.value)}
            error={!!formErrors.link}
            helperText={formErrors.link}
            margin="normal"
            required
            placeholder="https://example.com/payment"
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
        </>
      )
    }

    if (currentType === 'payment_credential') {
      return (
        <>
          <TextField
            fullWidth
            label="Platform Name"
            value={formData.platform_name || ''}
            onChange={(e) => handleInputChange('platform_name', e.target.value)}
            error={!!formErrors.platform_name}
            helperText={formErrors.platform_name}
            margin="normal"
            required
            placeholder="e.g., Stripe, PayPal, bKash"
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Credential Detail"
            value={formData.credential_detail || ''}
            onChange={(e) => handleInputChange('credential_detail', e.target.value)}
            error={!!formErrors.credential_detail}
            helperText={formErrors.credential_detail}
            margin="normal"
            required
            multiline
            rows={4}
            placeholder="Enter payment credentials, API keys, or other relevant information"
          />
        </>
      )
    }
  }

  const getCurrentItems = () => {
    const currentType = tabs[activeTab].value
    if (currentType === 'bank_account') return bankAccounts
    if (currentType === 'mfs_account') return mfsAccounts
    if (currentType === 'payment_link') return paymentLinks
    if (currentType === 'payment_credential') return paymentCredentials
    return []
  }

  const addButtonLabel = () => {
    const currentType = tabs[activeTab].value
    if (currentType === 'mfs_account') return 'Add MFS Number'
    const label = tabs[activeTab].label
    if (label.endsWith('s')) return `Add ${label.slice(0, -1)}`
    return `Add ${label}`
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Payment Settings
        </Typography>
        {canWrite && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c70000' } }}
          >
            {addButtonLabel()}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {tabs.map((tab, index) => {
              const Icon = tab.icon
              return (
                <Tab
                  key={tab.value}
                  label={tab.label}
                  icon={<Icon />}
                  iconPosition="start"
                />
              )
            })}
          </Tabs>
        </Box>

        <CardContent>
          {renderTable(getCurrentItems(), tabs[activeTab].value)}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit' : 'Add'} {tabs[activeTab].label.slice(0, -1)}
        </DialogTitle>
        <DialogContent>
          {dialogError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          ) : null}
          {renderForm()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c70000' } }}
          >
            {submitting ? <CircularProgress size={20} /> : editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this payment setting? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(auditItem)} onClose={handleCloseAudit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Audit log{settingLabel(auditItem) ? ` · ${settingLabel(auditItem)}` : ''}
        </DialogTitle>
        <DialogContent>
          {auditError ? (
            <Alert severity="error" sx={{ my: 2 }} onClose={() => setAuditError(null)}>
              {auditError}
            </Alert>
          ) : null}
          {auditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : auditLogs.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No create or update events recorded yet.
            </Typography>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Who</TableCell>
                  <TableCell>When</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={auditActionLabel(log.action)}
                        color={log.action === 'PAYMENT_SETTINGS_CREATED' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{log.performed_by_email || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAudit}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Container>
  )
}

export default PaymentSettingsPage
